import { useMediaQuery, useTheme } from '@mui/material';
import { AppBar, Toolbar, IconButton, Box, Tooltip } from '@mui/material';
import {
  Settings as SettingsIcon,
  Help as HelpIcon,
  AdminPanelSettings,
  PersonOutline,
  Build,
  Visibility,
} from '@mui/icons-material';
import { LAYOUT, Z_INDEX } from '@/constants/layout';
import { useLayoutStore } from '@/store/layoutStore';
import UserManagementDialog from '../dialogs/UserManagementDialog';
import LoginDialog from '../dialogs/LoginDialog';
import HelpDialog from '../dialogs/HelpDialog';
import { useAuthStore } from '../../store/authStore';
import React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

const NavUpPanel = () => {
  const theme = useTheme();
  const { isAuthenticated, username, logout, getRole } = useAuthStore();
  const [userDialogOpen, setUserDialogOpen] = React.useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = React.useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  // Shared layout state
  const { isDrawerCollapsed } = useLayoutStore();
  
  // Check if screen is small (mobile/tablet) - same as NavLeftPanel
  const isSmallScreen = useMediaQuery('(max-width:1400px)'); // Collapse at 1400px
  
  // Calculate drawer width based on screen size and manual collapse - same as NavLeftPanel
  const isActuallyCollapsed = isSmallScreen || isDrawerCollapsed;
  const drawerWidth = isActuallyCollapsed ? 64 : LAYOUT.DRAWER_WIDTH;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    logout();
    handleMenuClose();
  };
  const handleLogin = () => {
    setLoginDialogOpen(true);
    handleMenuClose();
  };

  const handleLoginDialogClose = () => {
    setLoginDialogOpen(false);
  };

  // Keyboard shortcut for help (F1)
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'F1') {
        event.preventDefault();
        setHelpDialogOpen(true);
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  const displayName = isAuthenticated() ? username : 'Public Viewer';
  const role = getRole();
  let UserIcon;
  switch (role) {
    case 'admin':
      UserIcon = AdminPanelSettings;
      break;
    case 'operator':
      UserIcon = Build;
      break;
    case 'viewer':
      UserIcon = PersonOutline;
      break;
    case 'public':
    default:
      UserIcon = Visibility;
      break;
  }

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: Z_INDEX.APP_BAR,
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          height: LAYOUT.APP_BAR_HEIGHT,
          transition: theme.transitions.create(['width', 'margin-left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title={displayName} arrow>
            <IconButton color="inherit" onClick={handleMenuOpen} size="large">
              <UserIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {isAuthenticated() ? (
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            ) : (
              <MenuItem onClick={handleLogin}>Login</MenuItem>
            )}
          </Menu>
          <IconButton color="inherit">
            <SettingsIcon />
          </IconButton>
          <Tooltip title="Help Documentation" arrow>
            <IconButton color="inherit" onClick={() => setHelpDialogOpen(true)}>
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <UserManagementDialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} />
      <LoginDialog open={loginDialogOpen} onClose={handleLoginDialogClose} />
      <HelpDialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />
    </>
  );
};

export default NavUpPanel;
